// src/components/pages/LoginPage.tsx
import React, {useEffect, useRef, useState} from "react";
import {Box, Button, Card, Image, ModalProps, PasswordInput, Text, TextInput, Title} from "@mantine/core";
import {googleAuthExchangeForNeurotuneAuth} from "@/common/services/authService.ts";
import {showErrorNotification, showSuccessNotification} from "../../common/notifications";
import classes from "./LoginPage.module.css";
import cx from "clsx";
import {modals} from "@mantine/modals";
import {getAuth, signInWithEmailAndPassword, sendPasswordResetEmail} from "@firebase/auth";
import {initializeApp} from '@firebase/app';
import {whoAmI} from "@/common/services/userService.ts";
import {isRedirectAuthMode, storePostLoginReturnTo} from "@/common/services/reauthRedirect";

// Initialize Firebase
const app = initializeApp({
    // https://firebase.google.com/docs/projects/api-keys
    // these are not sensitive information and are safe to be exposed in the
    // client side code
    apiKey: "AIzaSyC9JZlCrr-fseB6sncNcoxZXxh4YUkMJdg",
    authDomain: "bios-identity.firebaseapp.com",
});

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

/**
 * Opens a login modal with configurable options.
 *
 * @param {Object} options - The configuration options for the login modal.
 * @param {Function} options.onSuccess - Callback function to be executed upon successful login.
 * @param {string} [options.size="sm"] - The size of the modal. Default is "sm".
 * @param {string} [options.zIndex="1000"] - The CSS z-index of the modal. Default is "1000".
 * @param {boolean} [options.withCloseButton=false] - Whether the modal should display a close button. Default is false.
 * @param {Object} [options.others] - Additional properties to pass to the modal.
 * @returns {void}
 */
export const openLoginModal = ({
                                   onSuccess,
                                   size = "sm",
                                   zIndex = "1000",
                                   withCloseButton = false,
                                   ...others
                               }: Partial<ModalProps & {modalId: string, onSuccess: () => void }>) => {
    return modals.open({
        size,
        zIndex,
        withCloseButton,
        children: <LoginPage onSuccess={onSuccess}/>,
        ...others,
    })
}

interface GoogleLoginButtonProps {
    onSuccess: () => void;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({onSuccess}) => {
    const buttonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!window.google?.accounts?.id) {
            showErrorNotification({
                title: "Google Script Error",
                message: "Google script loaded but API not available"
            });
            return;
        }
        // login_uri must stay EXACTLY the URI registered in Google Cloud
        // Console — Google matches redirect URIs character-for-character
        // (query string included), so nothing may be appended here. The
        // post-login deep link travels via web storage instead (consumed by
        // LoadingPage after a successful bootstrap).
        if (isRedirectAuthMode()) {
            storePostLoginReturnTo();
        }
        const redirectMode = isRedirectAuthMode();
        const login_uri = redirectMode ? window.BIOS.neurotune_base_url + "/auth" : undefined;

        // Note: For redirect mode to work, the login_uri must be registered in Google Cloud Console
        // as an authorized redirect URI. If you get a redirect_uri_mismatch error, either:
        // 1. Add the URI to Google Cloud Console (APIs & Services > Credentials), OR
        // 2. Use popup mode by adding ?lm=popup to the URL

        window.google.accounts.id.initialize({
            client_id: "387658322212-g7svad8embeh8locoq95rs67guga4dbv.apps.googleusercontent.com",
            callback: async (response) => {
                try {
                    await googleAuthExchangeForNeurotuneAuth(response.credential);
                    // update who user is before we signal success
                    await whoAmI().catch(() => {
                        // ignore errors
                    });
                    onSuccess();
                } catch (err: any) {
                    // Error already shown in service so just eat it here
                    // to prevent unhandled promise rejection
                }
            },
            ux_mode: redirectMode ? "redirect" : "popup",
            // For redirect mode, use login_uri (where Google POSTs the credential)
            // For popup mode, the callback above handles the response
            login_uri: login_uri
        });

        window.google.accounts.id.renderButton(buttonRef.current!, {
            theme: "outline",
            size: "large",
            text: "signin_with",
            shape: "rectangular",
            logo_alignment: "left",
        });

        // Optional: show full login screen immediately
        window.google.accounts.id.prompt();
    }, [onSuccess]);

    return (
        <Box>
            <div ref={buttonRef}/>
        </Box>
    );
};

interface LoginPageProps {
    onSuccess?: () => void
    mode?: "redirect" | "popup"
}

const handleLoginSuccess = () => {
    // Check for redirect query param
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');

    if (redirect)
        window.location.href = redirect;
    else
        window.location.href = "/"
};
export const LoginPage: React.FC<LoginPageProps> = ({onSuccess = handleLoginSuccess}: LoginPageProps) => {

    const [showEmailLogin, setShowEmailLogin] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const handleEmailLogin = async () => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const idToken = await userCredential.user.getIdToken();
            await googleAuthExchangeForNeurotuneAuth(idToken);
            // update who user is
            await whoAmI().catch(() => {
                // ignore errors
            });
            await onSuccess();
        } catch (error: any) {
            // error already displayed and onSuccess is aborted so there is
            // nothing to do here.
        }
    };
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const renderPasswordReset = () => {
        return (
            <Box style={{width: "100%", display: "grid", grid: "auto / 1fr", rowGap: "0.5rem"}}>
                <TextInput
                    placeholder="Email"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                />
                <Button disabled={!emailValid} variant="outline"
                        bg={"var(--bios-brand-secondary-light)"}
                        onClick={() =>
                            sendPasswordResetEmail(auth, email).then(() => showSuccessNotification({
                                title: "Password Reset Email Sent",
                                message: "Please check your email for instructions to reset your password."
                            }))
                        }
                >Send Reset Link</Button>
                <Box style={{borderTop:"var(--bios-border)"}}/>
                <a style={{justifySelf: "start", cursor: "pointer"}}
                   onClick={() => setShowPasswordReset(false)}>
                    Back
                </a>
            </Box>
        )
    }
    const renderEmailLogin = () => {
        return (
            <Box style={{width: "100%", display: "grid", grid: "auto / 1fr", rowGap: "0.5rem"}}>
                <TextInput
                    placeholder="Email"
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                />
                <PasswordInput
                    placeholder="Password"
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                />
                <Button fullWidth variant="outline" disabled={!emailValid && !!password}
                        onClick={handleEmailLogin} bg={"var(--bios-brand-secondary-light)"}>
                    Login
                </Button>
                <a style={{justifySelf: "end", cursor: "pointer"}}
                   onClick={() => setShowPasswordReset(true)}>
                    Forgot password?
                </a>
            </Box>
        )
    }

    return (
        <Box style={{
            height: "100%",
            display: "grid",
            grid: "1fr / 1fr",
            justifyItems: "center",
            alignItems: "center",
        }}>
            <Card shadow="sm" w={400} p={0}>
                <Box className={cx(classes.root)}>
                    <Image className={cx(classes.logo)} src="/assets/images/BIOS_Logo_White_RGB.png"
                           alt="Neurotune Logo"
                           width={100}/>
                    <Title c="var(--bios-light-text)" order={1} mb="md">Neurotune</Title>
                    <GoogleLoginButton onSuccess={onSuccess}/>
                    <Text c="var(--bios-light-text)" ta="center">Or</Text>
                    {showPasswordReset ? renderPasswordReset() : showEmailLogin ? renderEmailLogin() :
                        (
                            <Button fullWidth variant="outline" bg={"var(--bios-brand-secondary-light)"}
                                    onClick={() => setShowEmailLogin(true)}>
                                Sign in with Email
                            </Button>
                        )
                    }
                </Box>
            </Card>
        </Box>
    );
};